import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Quote, Sparkles } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { BreadcrumbSchema } from '@/components/seo/BreadcrumbSchema';
import {
  CATEGORIES,
  CATEGORY_ORDER,
  type CategoryContent,
  type CategorySlug,
} from '@/data/livingAnnouncementExamples';

interface Props {
  category: CategoryContent;
}

// Themed atmospheric gradient — uses semantic tokens so light/dark both work.
const ATMOSPHERE: Record<string, string> = {
  'warm-amber':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--primary) / 0.18), transparent 70%)',
  'soft-gold':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--accent) / 0.18), transparent 70%)',
  'dusk-rose':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--secondary) / 0.22), transparent 70%)',
  'champagne-glow':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--primary) / 0.16), transparent 70%)',
  'rose-amber':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--secondary) / 0.18), transparent 70%)',
  'hearth-glow':
    'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--accent) / 0.16), transparent 70%)',
};

export default function LivingAnnouncementCategory({ category }: Props) {
  const path = `/living-announcements/${category.slug}`;
  const ogImageUrl = `https://say-it-now.app/og/living-announcements-${category.slug}.jpg`;

  useDocumentHead({
    title: category.metaTitle,
    description: category.metaDescription,
    path,
    meta: [
      { property: 'og:type', content: 'article' },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      {
        name: 'keywords',
        content: `living announcement, ${category.slug} message, ${category.slug} announcement examples, say it now, celebrate people while they're alive`,
      },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:type', content: 'image/jpeg' },
      { property: 'og:image:alt', content: category.h1 },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: ogImageUrl },
      { name: 'twitter:image:alt', content: category.h1 },
    ],
  });

  const related: CategorySlug[] = CATEGORY_ORDER.filter((s) => s !== category.slug).slice(0, 4);
  const atmosphere = ATMOSPHERE[category.atmosphere] ?? ATMOSPHERE['warm-amber'];

  // CollectionPage JSON-LD with each example as a CreativeWork — gives AI/search
  // crawlable structured data about the gallery without exposing private user content.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `https://say-it-now.app${path}#page`,
        url: `https://say-it-now.app${path}`,
        name: category.h1,
        description: category.metaDescription,
        inLanguage: 'en',
        image: ogImageUrl,
        primaryImageOfPage: { '@type': 'ImageObject', url: ogImageUrl, width: 1200, height: 630 },
        isPartOf: { '@type': 'WebSite', name: 'Say It NOW', url: 'https://say-it-now.app' },
        about: {
          '@type': 'Thing',
          name: `${category.slug} living announcement`,
        },
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: category.examples.map((ex, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'CreativeWork',
              name: ex.headline,
              author: { '@type': 'Person', name: ex.attribution },
              text: ex.body,
              about: category.slug,
            },
          })),
        },
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: 'Living Announcements', path: '/living-announcements' },
          { name: category.h1, path },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Header />

      <main className="flex-1">
        {/* Cinematic hero */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: atmosphere }}
          />
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
            }}
          />

          <div className="relative container max-w-4xl px-5 pt-20 pb-16 md:pt-32 md:pb-24 text-center">
            <p
              className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-primary/80 mb-6 motion-safe:animate-fade-in"
              style={{ animationDelay: '0ms' }}
            >
              {category.eyebrow}
            </p>
            <h1
              className="font-heading text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground mb-8 motion-safe:animate-fade-in"
              style={{ animationDelay: '120ms' }}
            >
              {category.h1}
            </h1>
            <p
              className="text-lg md:text-2xl leading-relaxed text-muted-foreground max-w-2xl mx-auto motion-safe:animate-fade-in"
              style={{ animationDelay: '240ms' }}
            >
              {category.lede}
            </p>

            <div
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 motion-safe:animate-fade-in"
              style={{ animationDelay: '360ms' }}
            >
              <Button asChild size="lg" className="rounded-full px-7 h-12 gap-2 w-full sm:w-auto">
                <Link to="/signup">
                  <Heart className="w-4 h-4" aria-hidden />
                  Create your own {category.slug} announcement
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="rounded-full px-6 h-12 text-muted-foreground hover:text-foreground"
              >
                <Link to="/living-announcements">
                  Browse all categories <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Hairline divider */}
        <div className="container max-w-4xl px-5">
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>

        {/* Long-form intro — readable, AI-indexable */}
        <section className="container max-w-2xl px-5 py-14 md:py-20">
          {category.intro.map((para, i) => (
            <p
              key={i}
              className="text-lg md:text-xl leading-[1.8] text-foreground/85 mb-6 last:mb-0"
            >
              {para}
            </p>
          ))}
        </section>

        {/* Gallery — cinematic example cards */}
        <section className="container max-w-5xl px-5 pb-16 md:pb-24">
          <header className="mb-10 md:mb-14 text-center">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-3">
              Examples
            </p>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight">
              Real moments. Real words.
            </h2>
          </header>

          <ul className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {category.examples.map((ex, i) => (
              <li
                key={i}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card/60 backdrop-blur-sm
                  transition-all duration-500 hover:border-primary/40 hover:-translate-y-1
                  motion-safe:animate-fade-in"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <article className="p-6 md:p-8 flex flex-col h-full">
                  <Quote
                    className="w-5 h-5 text-primary/60 mb-4 shrink-0"
                    aria-hidden
                  />
                  <h3 className="font-heading text-xl md:text-2xl leading-snug text-foreground mb-3">
                    {ex.headline}
                  </h3>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-5">
                    {ex.attribution}
                  </p>
                  <blockquote className="text-lg md:text-xl leading-snug font-heading text-foreground/95 italic mb-5 border-l-2 border-primary/50 pl-4">
                    “{ex.pullQuote}”
                  </blockquote>
                  <p className="text-[15px] leading-[1.75] text-foreground/80 flex-1">{ex.body}</p>
                </article>
                <div
                  aria-hidden
                  className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </li>
            ))}
          </ul>
        </section>

        {/* When to send + how to write */}
        <section className="container max-w-4xl px-5 pb-16 md:pb-24">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-4">
                When to send
              </p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6">
                The moments this matters most.
              </h2>
              <ul className="space-y-3">
                {category.whenToSend.map((line, i) => (
                  <li key={i} className="flex gap-3 text-foreground/85 leading-relaxed">
                    <Sparkles className="w-4 h-4 mt-1.5 text-primary/70 shrink-0" aria-hidden />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-4">
                How to write one
              </p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6">
                A short, honest guide.
              </h2>
              <ul className="space-y-3">
                {category.howToWrite.map((line, i) => (
                  <li key={i} className="flex gap-3 text-foreground/85 leading-relaxed">
                    <Heart className="w-4 h-4 mt-1.5 text-primary/70 shrink-0" aria-hidden />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Closing thought + CTA */}
        <section className="container max-w-3xl px-5 pb-16 md:pb-24">
          <div className="border-t border-border pt-12 md:pt-16 text-center">
            <p className="font-heading text-2xl md:text-4xl leading-tight text-foreground mb-3 tracking-tight">
              {category.closingThought}
            </p>
            <p className="text-sm uppercase tracking-[0.28em] text-muted-foreground mb-8">
              Celebrate people while they’re alive.
            </p>
            <Button asChild size="lg" className="rounded-full px-7 h-12 gap-2">
              <Link to="/signup">
                <Heart className="w-4 h-4" aria-hidden /> Start your living announcement
              </Link>
            </Button>
          </div>
        </section>

        {/* Related categories — internal linking */}
        <section className="container max-w-4xl px-5 pb-20">
          <h2 className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-5">
            More categories
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {related.map((slug) => {
              const r = CATEGORIES[slug];
              return (
                <li key={slug}>
                  <Link
                    to={`/living-announcements/${slug}`}
                    className="group flex items-center justify-between gap-4 p-5 rounded-xl border border-border hover:border-primary/40 hover:bg-accent/20 transition-colors"
                  >
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-1">
                        {r.eyebrow.split('·').pop()?.trim()}
                      </span>
                      <span className="block font-heading text-lg leading-snug text-foreground">
                        {r.h1}
                      </span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-primary shrink-0 transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </main>

      <Footer />
    </div>
  );
}
